// Import necessary dependencies and components
import React from "react";
import { useNavigate } from "react-router-dom";
import { Col, Badge } from "reactstrap";
import { MdRestore, MdVisibility, MdCalendarToday, MdAccessTime } from "react-icons/md";
import ButtonSubmit from "../../../components/Buttons/ButtonStyle";
import ImagePlaceHolder from "../../../assets/images/placeholder/ImagePlaceHolder.png";
import LoaderEventItem from "../../../components/Loader/LoaderEventItem";
import PropTypes from "prop-types";

const AllAttendeeEvents = ({
  userEmail,
  filteredAttendeEvents,
  isLoadingGetEvents,
  showArchives = false,
  onRestore,
  restoringEventId,
  isRestoring = false
}) => {
  const Navigate = useNavigate();
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  // ✅ Helper: Format date as "Mon 28 Dec 2026"
  const formatDate = (isoString) => {
    if (!isoString) return "TBA";
    const date = new Date(isoString);
    const day = dayNames[date.getUTCDay()];
    const month = monthNames[date.getUTCMonth()];
    const year = date.getUTCFullYear();
    const dateNum = date.getUTCDate();
    return `${day} ${dateNum} ${month} ${year}`;
  };

  // ✅ Helper: Format time as "09:00 AM"
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setUTCHours(hours, minutes);
    return date.toLocaleTimeString('en-ZA', { 
      hour: '2-digit', 
      minute: '2-digit', 
      hour12: true 
    });
  };

  // ✅ Helper: Check if two dates are the same day
  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getUTCFullYear() === d2.getUTCFullYear() &&
           d1.getUTCMonth() === d2.getUTCMonth() &&
           d1.getUTCDate() === d2.getUTCDate();
  };

  const checkForEmail = (personnelArray, targetEmail) => {
    return personnelArray?.some((person) => person?.email === targetEmail);
  };

  const handleAttendeeEnterEvent = (eventName, evID) => {
    Navigate(`/${eventName?.replace(/ /g, "-")}/event-landing/${evID}`);
  };

  const handleEditEvent = (eventName, evID) => {
    Navigate(`/${eventName?.replace(/ /g, "-")}/set/${evID}`);
  };

  const handleRestoreClick = (eventId, eventName) => {
    if (onRestore && window.confirm(`Are you sure you want to restore "${eventName}"?`)) {
      onRestore(eventId);
    }
  };

  // ✅ Loading state - return early
  if (isLoadingGetEvents) {
    return <LoaderEventItem />;
  }

  // ✅ SAFETY FILTER: Guarantee archive/active separation
  const safeFilteredEvents = (filteredAttendeEvents || []).filter(event => {
    const isArchived = !!event.deletedAt;
    return showArchives ? isArchived : !isArchived;
  });

  // ✅ Empty state check AFTER filtering
  if (!safeFilteredEvents || safeFilteredEvents.length === 0) {
    return (
      <Col xs={12}>
        <div className="text-center py-5 text-muted">
          <p className="mb-0">
            {showArchives ? "archived" : "Active Events"}
          </p>
        </div>
      </Col>
    );
  }

  return (
    <>
      {safeFilteredEvents.map((item, index) => {
        const isAdmin = checkForEmail(item?.personnel, userEmail);
        
        // ✅ Format start and end dates
        const startDateStr = formatDate(item.startDate);
        const endDateStr = item.endDate ? formatDate(item.endDate) : null;
        const isMultiDay = item.endDate && !isSameDay(item.startDate, item.endDate);
        
        // ✅ Format times
        const startTimeStr = formatTime(item.startTime);
        const endTimeStr = formatTime(item.endTime);

        return (
          <Col xs={12} sm={12} md={12} lg={12} key={item._id || index}>
            <div className="d-flex p-3 mb-4 shadow-sm bg-white card-brd myEvent-card-container position-relative">
              
              {/* ✅ Archived Badge */}
              {showArchives && (
                <Badge 
                  color="warning" 
                  className="position-absolute top-0 end-0 m-2"
                  style={{ 
                    fontSize: '10px', 
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <MdRestore size={12} />
                  Archived
                </Badge>
              )}

              <div className="d-flex align-items-center" style={{ flex: 0.4 }}>
                <div className="MyEvent_Parent_div pt-3">
                  <div className="MyEvent_image_div">
                    <img
                      src={item?.bannerImage ? item?.bannerImage : ImagePlaceHolder}
                      width="100%"
                      height="100%"
                      className="card-brd"
                      alt={item?.eventName || "Event banner"}
                      style={{ objectFit: 'cover' }}
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
              
              <div className="w-100 mx-2">
                <div style={{ fontSize: 16, fontWeight: "600", color: "black" }}>
                  {item?.eventName}
                </div>
                
                <div className="mt-2" style={{ fontSize: 13 }}>
                  {item?.eventDescription && item?.eventDescription.length > 35
                    ? `${item?.eventDescription.substring(0, 200)}...`
                    : item?.eventDescription}
                </div>
                
                {/* ✅ PROFESSIONAL DATE/TIME DISPLAY */}
                <div className="mt-2 d-flex flex-column gap-1">
                  {/* Date Row */}
                  <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: 13 }}>
                    <MdCalendarToday size={14} className="text-warning" />
                    <span className="fw-medium text-dark">
                      {startDateStr}
                      {isMultiDay && <span className="text-muted"> – {endDateStr}</span>}
                    </span>
                  </div>
                  
                  {/* Time Row */}
                  {(item.startTime || item.endTime) && (
                    <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: 12 }}>
                      <MdAccessTime size={12} />
                      <span>
                        {startTimeStr}
                        {item.endTime && <span> – {endTimeStr}</span>}
                        <span className="ms-1" style={{ fontSize: 11, color: '#6c757d' }}>SAST</span>
                      </span>
                    </div>
                  )}
                </div>

                {/* ✅ Show archived date if available */}
                {showArchives && item.deletedAt && (
                  <div className="mt-2" style={{ fontSize: 11, color: '#6c757d' }}>
                    Archived: {new Date(item.deletedAt).toLocaleDateString()}
                  </div>
                )}
                
                <div className="mt-3 mb-1 d-flex flex-wrap gap-2">
                  {showArchives ? (
                    <>
                      {/* ✅ Restore Button for Archived Events */}
                      {onRestore && (
                        <button
                          onClick={() => handleRestoreClick(item._id, item.eventName)}
                          disabled={isRestoring && restoringEventId === item._id}
                          className="btn btn-success btn-sm d-flex align-items-center gap-1"
                          style={{ 
                            minHeight: '36px',
                            fontSize: '13px',
                            padding: '6px 12px'
                          }}
                          aria-label={`Restore event: ${item.eventName}`}
                        >
                          <MdRestore size={16} />
                          {isRestoring && restoringEventId === item._id 
                            ? 'Restoring...' 
                            : 'Restore'}
                        </button>
                      )}

                      {/* ✅ View Button for Archived Events */}
                      <button
                        onClick={() => handleAttendeeEnterEvent(item?.eventName, item?._id)}
                        className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                        style={{ 
                          minHeight: '36px',
                          fontSize: '13px',
                          padding: '6px 12px'
                        }}
                        aria-label={`View archived event: ${item.eventName}`}
                      >
                        <MdVisibility size={16} />
                        View
                      </button>
                    </>
                  ) : (
                    <>
                      {/* ✅ Standard Buttons for Active Events */}
                      <ButtonSubmit
                        Title="Visit Event"
                        ColorText="#F68A20"
                        BorderColor="#F68A20"
                        BackgroundColor="white"
                        borderRadius="25px"
                        handleOnclick={() =>
                          handleAttendeeEnterEvent(item?.eventName, item?._id)
                        }
                      />
                      
                      {isAdmin && (
                        <button
                          type="button"
                          className="btn btn-link text-decoration-underline p-0 ms-2"
                          style={{
                            fontSize: 14,
                            color: "#0e35dd",
                            fontWeight: "500",
                            cursor: "pointer",
                          }}
                          onClick={() => handleEditEvent(item?.eventName, item?._id)}
                          aria-label={`Edit event: ${item.eventName}`}
                        >
                          Edit Event
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </Col>
        );
      })}
    </>
  );
};

// ✅ PropTypes
AllAttendeeEvents.propTypes = {
  userEmail: PropTypes.string.isRequired,
  filteredAttendeEvents: PropTypes.array.isRequired,
  isLoadingGetEvents: PropTypes.bool.isRequired,
  showArchives: PropTypes.bool,
  onRestore: PropTypes.func,
  restoringEventId: PropTypes.string,
  isRestoring: PropTypes.bool,
};

export default AllAttendeeEvents;